import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import Cookies from "js-cookie";
const DB_NAME = "SurveyDB";
const STORE_NAME = "files";

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

const getFileFromIndexedDB = async (key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const deleteFileFromIndexedDB = async (key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const handlePaymentSuccess = async () => {
      const sessionId = searchParams.get("session_id");

      if (!sessionId) {
        navigate("/projects");
        return;
      }

      try {
        const token = Cookies.get("token");
        if (!token) {
          navigate("/log-in");
          return;
        }

        //step 0: Fetch Stripe Checkout Session
        const checkoutRes = await fetch(
          `${process.env.REACT_APP_API_URL}/payment/checkout-session/${sessionId}`,
          {
            method: "GET",
            headers: { token },
          },
        );

        const checkoutData = await checkoutRes.json();

        if (
          !checkoutRes.ok ||
          !checkoutData.amount_total ||
          checkoutData.payment_status !== "paid"
        ) {
          throw new Error("Failed to fetch checkout session");
        }

        const totalAmount = checkoutData.amount_total;

        //step 1: Identify Payment Source

        const paymentContext = JSON.parse(
          localStorage.getItem("paymentContext") || "{}",
        );

        const paymentSource = paymentContext.source || "NEW_USER";

        if (paymentSource === "CUSTOM_INVITE") {
          const waitForActivation = async () => {
            for (let attempt = 0; attempt < 8; attempt++) {
              try {
                const statusRes = await fetch(
                  `${process.env.REACT_APP_API_URL}/payment/custom-account-status`,
                  { headers: { token } },
                );
                const statusData = await statusRes.json();
                if (statusRes.ok && statusData?.data?.isPaid) {
                  return true;
                }
              } catch (e) {
                console.error("custom-account-status check failed:", e);
              }
              await new Promise((r) => setTimeout(r, 1500));
            }
            return false;
          };

          let activated = await waitForActivation();

          if (!activated) {
            try {
              const verifyRes = await fetch(
                `${process.env.REACT_APP_API_URL}/payment/verify-custom-session?session_id=${sessionId}`,
                { headers: { token } },
              );
              const verifyData = await verifyRes.json();
              if (verifyRes.ok && verifyData?.data?.activated) {
                activated = true;
              }
            } catch (e) {
              console.error("error", e);
            }
          }

          localStorage.removeItem("paymentContext");

          if (!activated) {
            // Payment succeeded with Stripe but our webhook hasn't
            // confirmed it yet (or failed). Do NOT unlock anything —
            // send the user to their dashboard, which will show the
            // Payment Pending state until the webhook catches up.
            setStatus("error");
            navigate("/projects");
            return;
          }

          setStatus("success");
          navigate("/projects");
          return;
        }

        if (paymentSource === "ADD_CREDITS") {
          const { newScore } = paymentContext;

          if (newScore) {
            await fetch(`${process.env.REACT_APP_API_URL}/user/addCredits`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token,
              },
              body: JSON.stringify({
                newScore,
                totalAmount,
              }),
            });
          }

          localStorage.removeItem("paymentContext");
          navigate("/subscription");
          return;
        }

        //CASE 2: UPGRADE PLAN

        if (paymentSource === "UPGRADE_PLAN") {
          const selectedPlanData = JSON.parse(
            localStorage.getItem("selectedPlan") || "{}",
          );

          const { planMapping } = await import("../assets/data/pricingData");
          const planDetails = planMapping[selectedPlanData.planName];

          if (planDetails) {
            await fetch(`${process.env.REACT_APP_API_URL}/user/updatePlans`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token,
              },
              body: JSON.stringify({
                plan_name: selectedPlanData.planName,
                credits_score: planDetails.credits_score,
                project_limits: planDetails.project_limits,
                question_count: planDetails.question_count,
                totalAmount,
                isUpgrade: true,
              }),
            });
          }

          localStorage.removeItem("selectedPlan");
          localStorage.removeItem("paymentContext");
          navigate("/subscription");
          return;
        }

        //CASE 3: NEW USER (Subscribe + Project Creation)

        const selectedPlanData = JSON.parse(
          localStorage.getItem("selectedPlan") || "{}",
        );

        const { planMapping } = await import("../assets/data/pricingData");
        const planDetails = planMapping[selectedPlanData.planName];

        if (!planDetails) {
          navigate("/projects");
          return;
        }

        await fetch(`${process.env.REACT_APP_API_URL}/user/subscribePlan`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({
            plan_name: selectedPlanData.planName,
            credits_score: planDetails.credits_score,
            project_limits: planDetails.project_limits,
            question_count: planDetails.question_count,
            totalAmount,
            isPaymentSuccess: true,
          }),
        });

        //step 2: Load Project Metadata

        let projectMetadata = null;

        const storedMetadata = sessionStorage.getItem("pendingProjectMetadata");

        if (storedMetadata) {
          projectMetadata = JSON.parse(storedMetadata);
        } else {
          const storedSurveyMeta = localStorage.getItem("surveyMeta");
          const storedAudienceData = localStorage.getItem("audienceData");

          if (storedSurveyMeta || storedAudienceData) {
            projectMetadata = {
              surveyMeta: storedSurveyMeta
                ? JSON.parse(storedSurveyMeta)
                : null,
              audienceData: storedAudienceData
                ? JSON.parse(storedAudienceData)
                : [],
            };
          }
        }

        if (
          !projectMetadata ||
          !projectMetadata.audienceData ||
          projectMetadata.audienceData.length === 0
        ) {
          navigate("/projects");
          return;
        }
        const resumeFlow = projectMetadata.flow || "uploaded";

        let surveyId, projectId, audianceGroupId, surveyName;

        if (resumeFlow === "generated") {
          // The generated survey has only been living in local storage up
          // to this point (the user wasn't authenticated when they
          // finished editing it). Now that payment succeeded, we know
          // they're a real, authenticated user -- save it for real here.
          const { requestId, finalSurvey } = projectMetadata;

          if (!requestId || !finalSurvey) {
            navigate("/projects");
            return;
          }

          const questionCount = (finalSurvey.sections || []).reduce(
            (sum, sec) =>
              sum + (Array.isArray(sec.questions) ? sec.questions.length : 0),
            0,
          );

          const saveRes = await fetch(
            `${process.env.REACT_APP_API_URL}/project/saveGeneratedSurvey`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", token },
              body: JSON.stringify({
                survey: finalSurvey,
                requestId,
                questionCount,
              }),
            },
          );

          const saveData = await saveRes.json();

          if (!saveRes.ok || !saveData.success) {
            navigate("/projects");
            return;
          }

          surveyId = saveData.surveyId;
          projectId = saveData.projectId;
          audianceGroupId = saveData.audianceGroupId;
          surveyName =
            projectMetadata.surveyName ||
            finalSurvey.title ||
            "Untitled Survey";
        } else {
          //step 3: Get Survey File from IndexedDB

          let surveyFile;
          try {
            surveyFile = await getFileFromIndexedDB("surveyFile");
          } catch {
            navigate("/projects");
            return;
          }

          if (!(surveyFile instanceof File)) {
            navigate("/projects");
            return;
          }

          //step 4: Upload Survey

          const totalAudienceSize = projectMetadata.audienceData.reduce(
            (sum, a) => sum + a.count,
            0,
          );

          const formData = new FormData();
          formData.append("file", surveyFile);
          formData.append("totalAudianceSize", totalAudienceSize);

          if (projectMetadata.surveyMeta) {
            formData.append(
              "questionCount",
              projectMetadata.surveyMeta.questionCount,
            );
            formData.append(
              "questionCountConfidence",
              projectMetadata.surveyMeta.questionCountConfidence,
            );
            formData.append(
              "surveyTheme",
              projectMetadata.surveyMeta.surveyTheme,
            );
          }

          const surveyRes = await fetch(
            `${process.env.REACT_APP_API_URL}/project/uploadSurvey`,
            {
              method: "POST",
              headers: { token },
              body: formData,
            },
          );

          const surveyUploadData = await surveyRes.json();

          if (!surveyRes.ok || !surveyUploadData.surveyId) {
            navigate("/projects");
            return;
          }

          surveyId = surveyUploadData.surveyId;
          projectId = surveyUploadData.projectId;
          audianceGroupId = surveyUploadData.audianceGroupId;
          surveyName =
            surveyFile.name?.replace(/\.[^/.]+$/, "") || "Untitled Survey";
        }

        //step 5: Create Audience

        await fetch(
          `${process.env.REACT_APP_API_URL}/project/createAndUpdateAudience`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token,
            },
            body: JSON.stringify({
              surveyId,
              audienceList: projectMetadata.audienceData,
            }),
          },
        );

        //step 6: Create Project

        await fetch(
          `${process.env.REACT_APP_API_URL}/project/createAndUpdateProject`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token,
            },
            body: JSON.stringify({
              projectId,
              surveyId,
              audianceGroupId,
              audienceDefinitions: projectMetadata.audienceData,
              surveyName,
            }),
          },
        );

        //  CLEANUP + REDIRECT

        localStorage.removeItem("selectedPlan");
        localStorage.removeItem("surveyMeta");
        localStorage.removeItem("audienceData");
        localStorage.removeItem("paymentContext");
        localStorage.removeItem("surveySpecifications");
        sessionStorage.removeItem("pendingProjectMetadata");

        try {
          await deleteFileFromIndexedDB("surveyFile");
        } catch {}

        navigate("/generate-insights", {
          state: {
            projectId,
            surveyId,
            audienceGroupId: audianceGroupId,
            surveyName,
            createdAt: new Date().toISOString(),
          },
        });
      } catch (err) {
        console.error("Payment success error:", err);
        navigate("/projects");
      }
    };

    handlePaymentSuccess();
  }, [searchParams, navigate]);

  return (
    <div
      className="page-bg"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "125vh",
      }}
    >
      <div className="create-survey-logo">
        <a href="/">
          <img
            src="/json-media/img/logo.svg"
            alt="GI"
            width={50}
            style={{ padding: 5 }}
          />
        </a>
      </div>

      <div className="content-center">
        <Box textAlign="center">
          {status === "processing" && (
            <>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 2rem",
                  animation: "scaleIn 0.5s ease-out",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2
                style={{
                  color: "#fff",
                  marginBottom: "1rem",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Payment Successful!
              </h2>
              <p
                style={{
                  color: "#94a3b8",
                  marginBottom: "2rem",
                  fontSize: "1rem",
                }}
              >
                Setting up your project...
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                }}
              >
                <CircularProgress size={20} sx={{ color: "#3b82f6" }} />
                <span style={{ color: "#3b82f6", fontSize: "0.95rem" }}>
                  Processing
                </span>
              </div>
            </>
          )}
          {status === "success" && (
            <>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 2rem",
                  animation: "scaleIn 0.5s ease-out",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2
                style={{
                  color: "#fff",
                  marginBottom: "1rem",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                All Set!
              </h2>
              <p
                style={{
                  color: "#94a3b8",
                  marginBottom: "2rem",
                  fontSize: "1rem",
                }}
              >
                Your project is being generated
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                }}
              >
                <CircularProgress size={20} sx={{ color: "#3b82f6" }} />
                <span style={{ color: "#3b82f6", fontSize: "0.95rem" }}>
                  Redirecting
                </span>
              </div>
            </>
          )}
          {status === "error" && (
            <>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 2rem",
                  animation: "scaleIn 0.5s ease-out",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6l12 12M6 18L18 6"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h2
                style={{
                  color: "#fff",
                  marginBottom: "1rem",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Something went wrong
              </h2>
              <p
                style={{
                  color: "#94a3b8",
                  marginBottom: "1rem",
                  fontSize: "0.9rem",
                }}
              >
                Please check your projects page or try creating your project
                again
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                }}
              >
                <CircularProgress size={20} sx={{ color: "#94a3b8" }} />
                <span style={{ color: "#94a3b8", fontSize: "0.95rem" }}>
                  Redirecting to projects
                </span>
              </div>
            </>
          )}
        </Box>
        <style jsx>{`
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PaymentSuccess;
